import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { toast } from 'react-toastify';
import { IoIosClose } from 'react-icons/io';

import Button from 'components/ui/button';
import { useModal } from 'context/ModalContext';
import Box from 'components/ui/box';
import { updateTask } from 'services/dashboard.service';
import { useAuth } from 'context/AuthContext';
import { actionLog } from 'services/actionlog.service';
import Tippy from '@tippyjs/react';
import { getPerformanceCategory } from 'utils/helpers';
import Humanograma from 'components/Humanograma';
import { UserAvatar } from 'components/user-avatar';

const useFocusTrap = (ref: React.RefObject<HTMLDivElement>, isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !ref.current) return;
      event.preventDefault();

      const focusableElements = ref.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const focusableArray = Array.from(focusableElements) as HTMLElement[];
      const currentIndex = focusableArray.indexOf(document.activeElement as HTMLElement);

      let nextIndex: number;
      if (event.shiftKey) {
        nextIndex = currentIndex <= 0 ? focusableArray.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex >= focusableArray.length - 1 ? 0 : currentIndex + 1;
      }

      focusableArray[nextIndex]?.focus();
    };

    const timeoutId = setTimeout(() => {
      if (ref.current) {
        const firstFocusable = ref.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        firstFocusable?.focus();
      }
    }, 50);

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, ref, onClose]);
};

interface Props {
  callback: (obj: { id: string; message?: string }) => void;
  id: string;
  status: string | undefined;
  userToChange: string | undefined;
  data?: any;
}

export default function ModalTeamMemberDecrease(props: Props) {
  ReactModal.setAppElement('#modal');

  const modalID = 'team-member-decrease';

  const { openModal, closeModal } = useModal();
  const { user } = useAuth();
  const { callback, status, id, userToChange, data } = props;

  const [modalIsOpen, setIsOpen] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [defaultPercent, setDefaultPercent] = useState<number | undefined>();
  const [newPercent, setNewPercent] = useState<number | undefined>();
  const modalContentRef = useRef<HTMLDivElement>(null);

  function handleCloseModal() {
    //
  }

  const handleFocusTrapClose = useCallback(() => {
    if (!sending) closeModal();
  }, [sending, closeModal]);

  useFocusTrap(modalContentRef, modalIsOpen, handleFocusTrapClose);

  function afterOpenModal() {
    //
  }

  const member = useMemo(() => {
    return data?.target?.find((x: { _id: string | undefined }) => x._id === userToChange);
  }, [data, userToChange]);

  async function handleSubmit() {
    if (sending || !member || !newPercent) return false;

    setSending(true);

    try {
      const { action } = await actionLog('me', 'task_team_member_change_percent', {
        taskId: data._id,
        percent: newPercent,
        player: userToChange,
      });

      const _success = action.attributes.status === 'OK';

      if (_success) {
        setSending(false);
        callback({ id: 'reload_task' });
        closeModal();

        toast.success(`Alteração realizada com sucesso.`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else {
        toast.error(`Ocorreu um erro ao registrar sua ação. Por favor, tente novamente em alguns minutos.`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        setSending(false);
      }
    } catch (error) {
      toast.error(`Ocorreu um erro ao registrar sua ação. Por favor, tente novamente em alguns minutos.`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      setSending(false);
    }
  }

  useEffect(() => {
    if (!!userToChange && data && typeof id === 'string' && typeof openModal === 'string' && openModal === `${modalID}-${id}`) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }

    setNewPercent(undefined);
  }, [id, openModal, data, userToChange]);

  useEffect(() => {
    if (modalIsOpen && data && userToChange) {
      setDefaultPercent(data.team?.find((x: { _id: string | undefined }) => x._id === userToChange)?.percent || 0);
    }
  }, [modalIsOpen, data, userToChange]);

  if (!modalIsOpen || !member || !defaultPercent || defaultPercent <= 10) return <></>;

  return (
    <>
      <ReactModal
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={true}
        isOpen={modalIsOpen}
        onAfterOpen={afterOpenModal}
        onRequestClose={handleCloseModal}
        contentLabel="Avaliação"
        portalClassName="relative z-50"
        overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-60 flex flex-col items-center w-screen h-screen p-6 pt-[10vh] overflow-y-auto"
        contentElement={() => (
          <div ref={modalContentRef} role="dialog" aria-modal="true" aria-labelledby="team-member-decrease-ecoa-modal-title">
            <Box className={`w-[90%] lg:w-auto lg:max-w-[600px]`}>
              <Button
                color="primary"
                className="absolute -right-5 -top-5 rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center"
                onClick={() => {
                  if (!sending) closeModal();
                }}
                disabled={sending}
                aria-label="Fechar modal"
              >
                <IoIosClose color="white" size={40} aria-hidden="true" />
              </Button>

              <>
                <div className="w-full lg:w-[536px] flex flex-col items-center justify-center space-y-6">
                  <h4 tabIndex={0} id="team-member-decrease-ecoa-modal-title" className="block text-center text-yellow text-lg font-semibold leading-none">
                    Reduzir percentual de atuação
                  </h4>
                  <p className="text-alice-blue block text-sm font-thin" tabIndex={0}>
                    Adapte os percentuais, conforme a natureza da realização, a complexidade da atuação e as expectativas
                    específicas.
                  </p>
                  <div className={`bg-maastricht-blue-3 p-3 rounded-lg space-y-5 w-full`}>
                    <p className="text-alice-blue block text-sm font-thin block text-left w-full" tabIndex={0}>
                      <strong tabIndex={0} className="block">Título da Realização Destaque:</strong>
                    </p>
                    <p className="text-alice-blue block text-sm font-thin block text-left w-full !mt-0" tabIndex={0}>
                      <span tabIndex={0} className="text-electric-blue block">{data.title}.</span>
                    </p>

                    <p className="text-alice-blue block text-sm font-thin block text-left w-full" tabIndex={0}>
                      <strong tabIndex={0} className="block">Participante:</strong>
                    </p>
                    <div className="flex flex-col lg:flex-row lg:items-center space-y-2 lg:space-y-0 lg:space-x-4 !mt-1">
                      <div className="flex-1 flex flex-row items-center text-white space-x-3">
                        <UserAvatar data={{ ...member, ...member.extra }} />
                        <Humanograma data={{ ...member.extra, _id: member._id, nome: member.name }}>
                          <span tabIndex={0} className="text-base font-thin text-white m-0">
                            <span tabIndex={0} className="font-light">{member.name.split(' ')[0]}</span> ({member._id})
                          </span>
                        </Humanograma>
                      </div>
                      <div tabIndex={0} className="bg-oxford-blue px-4 py-2 text-xs font-thin leading-none text-yellow rounded-full flex items-center">
                        {defaultPercent}% de atuação
                      </div>
                    </div>
                  </div>
                  <div className="w-full space-y-4">
                    <p tabIndex={0}>Novo percentual de atuação:</p>
                    <div className="flex flex-row items-center justify-center space-x-3">
                      {[25, 50, 75, 100].map((e) => {
                        const buttonValue = e;

                        if (defaultPercent && buttonValue >= defaultPercent) return null;

                        return (
                          <Tippy
                            placement="bottom"
                            allowHTML={true}
                            className="bg-black"
                            maxWidth={300}
                            content={<div tabIndex={0}>{getPerformanceCategory(buttonValue)}</div>}
                          >
                            <button
                              key={buttonValue}
                              onClick={() => setNewPercent(buttonValue)}
                              className={`text-base leading-none disabled:opacity-50 ring-2 w-10 h-10 ring-slate-blue flex flex-col justify-center lg:flex-row items-center ${
                                buttonValue === newPercent ? 'bg-slate-blue text-white' : 'bg-transparent text-white'
                              } rounded py-1 px-1`}
                            >
                              {buttonValue === newPercent ? buttonValue + '%' : buttonValue}
                            </button>
                          </Tippy>
                        );
                      })}
                    </div>
                    <div tabIndex={0} className="text-electric-blue text-xs font-thin">
                      {newPercent ? getPerformanceCategory(newPercent) : ''}
                    </div>
                  </div>
                  <button
                    className={`text-white bg-slate-blue text-sm inline-flex items-center justify-center font-semibold uppercase rounded py-3 px-4 leading-none disabled:opacity-70`}
                    onClick={() => {
                      handleSubmit();
                    }}
                    disabled={sending || !newPercent}
                  >
                    CONFIRMAR
                  </button>
                </div>
              </>
            </Box>
          </div>
        )}
      />
    </>
  );
}


